import { useDocumentTitle } from '@/hooks/useDocumentTitle';

const clauses = [
  {
    title: '1. Introduction',
    content: `Straton Group (PTY) Ltd ("Straton Group", "we", "us", or "our") is committed to protecting your personal information in accordance with the Protection of Personal Information Act 4 of 2013 ("POPIA") and any other applicable South African legislation.

This Privacy Policy explains how we collect, use, store, share, and protect your personal information when you visit our website at www.stratongroup.co.za ("the Website"), engage with our services, or otherwise interact with us.

By using our Website or services, you acknowledge that you have read and understood this Privacy Policy.`,
  },
  {
    title: '2. Who We Are',
    content: `Straton Group (PTY) Ltd is the responsible party as defined in POPIA. We are responsible for determining the purpose and means of processing your personal information.

For any data protection enquiries, please contact us through the details provided in clause 14 below.`,
  },
  {
    title: '3. What Personal Information We Collect',
    content: `We may collect the following categories of personal information:

3.1. Identity information: name, surname, job title, company name.
3.2. Contact information: email address, telephone number, physical address.
3.3. Technical information: IP address, browser type and version, time zone, browser plug-in types, operating system, and platform.
3.4. Usage information: information about how you use our Website and services.
3.5. Communication information: any information you provide when you contact us via our Website, email, or telephone.
3.6. Career information: CV, qualifications, employment history, and references submitted through our Careers section.`,
  },
  {
    title: '4. How We Collect Personal Information',
    content: `We collect personal information through the following means:

4.1. Directly from you when you complete contact forms, submit a job application, communicate with us by email or telephone, or request information about our services.
4.2. Automatically through our Website using cookies and similar technologies (see clause 9 below).
4.3. From third parties, including recruitment platforms, professional networking sites, and referral partners, where permitted by applicable law.`,
  },
  {
    title: '5. Purpose of Processing',
    content: `We process your personal information for the following purposes:

5.1. To respond to your enquiries and provide information about our services.
5.2. To fulfil contractual obligations and deliver our services to clients.
5.3. To process job applications and manage recruitment.
5.4. To improve and maintain our Website and services.
5.5. To comply with legal and regulatory obligations.
5.6. To send marketing communications where you have provided consent or where we have a legitimate interest in doing so, subject to your right to object.
5.7. To protect the rights, property, and safety of Straton Group, our clients, and others.`,
  },
  {
    title: '6. Legal Basis for Processing',
    content: `We process your personal information on one or more of the following legal grounds:

6.1. Your consent.
6.2. Performance of a contract to which you are a party.
6.3. Compliance with a legal obligation.
6.4. Our legitimate interests, provided these are not overridden by your rights and interests.`,
  },
  {
    title: '7. Sharing of Personal Information',
    content: `We do not sell your personal information to third parties. We may share your personal information with:

7.1. Service providers who assist us in operating our Website and delivering our services, subject to appropriate data processing agreements.
7.2. Professional advisors, including lawyers, accountants, and auditors, where necessary.
7.3. Regulatory authorities, law enforcement agencies, or courts where required by law.
7.4. Potential buyers or investors in the event of a merger, acquisition, or sale of all or part of our business, subject to appropriate confidentiality obligations.

Where we transfer personal information outside South Africa, we will take appropriate safeguards to ensure such transfers comply with POPIA.`,
  },
  {
    title: '8. Retention of Personal Information',
    content: `We retain your personal information only for as long as is necessary for the purposes set out in this Privacy Policy, unless a longer retention period is required by law. When personal information is no longer required, we will securely delete or anonymise it.`,
  },
  {
    title: '9. Cookies',
    content: `Our Website uses cookies and similar tracking technologies to enhance your browsing experience, analyse Website traffic, and understand where our visitors are coming from.

9.1. Essential cookies: necessary for the Website to function and cannot be disabled.
9.2. Analytics cookies: help us understand how visitors interact with our Website. We use this information to improve our Website.
9.3. Marketing cookies: used to track visitors across websites to display relevant advertisements.

You can control cookies through your browser settings. Please note that disabling certain cookies may affect the functionality of our Website.`,
  },
  {
    title: '10. Your Rights Under POPIA',
    content: `As a data subject, you have the following rights:

10.1. The right to be notified that your personal information is being collected and the purpose thereof.
10.2. The right to access your personal information held by us.
10.3. The right to request correction or deletion of your personal information.
10.4. The right to object to the processing of your personal information.
10.5. The right to lodge a complaint with the Information Regulator of South Africa.

To exercise any of these rights, please contact us using the details provided in clause 14 below.

Information Regulator contact details: www.justice.gov.za/inforeg/ or inforeg@justice.gov.za`,
  },
  {
    title: '11. Security',
    content: `We implement appropriate technical and organisational measures to protect your personal information against unauthorised access, disclosure, alteration, or destruction. However, no method of transmission over the internet or electronic storage is completely secure, and we cannot guarantee absolute security.`,
  },
  {
    title: '12. Children',
    content: `Our Website and services are not directed at children under the age of 18. We do not knowingly collect personal information from children. If you believe we have inadvertently collected information from a child, please contact us immediately.`,
  },
  {
    title: '13. Changes to This Privacy Policy',
    content: `We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the updated policy on our Website with a revised effective date. Your continued use of our Website after any changes constitutes your acceptance of the updated Privacy Policy.`,
  },
  {
    title: '14. Contact Us',
    content: `If you have any questions, concerns, or complaints about this Privacy Policy or how we handle your personal information, please contact:

Straton Group (PTY) Ltd
Website: www.stratongroup.co.za`,
  },
];

export default function PrivacyPolicy() {
  useDocumentTitle('Privacy Policy — Straton Group');

  return (
    <main id="main-content" className="bg-bg-primary min-h-screen">
      {/* Header */}
      <section className="section-padding pt-32 pb-12">
        <div className="max-w-[1400px] mx-auto">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-8 h-px bg-text-muted" />
            <span className="text-xs uppercase tracking-[0.2em] text-text-muted">
              Legal
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold uppercase tracking-[0.15em] text-text-primary mb-4">
            Privacy Policy
          </h1>
          <p className="text-sm text-text-muted">
            Straton Group (PTY) Ltd — Effective Date: 1 June 2026
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="section-padding pb-24">
        <div className="max-w-[900px] mx-auto">
          <div className="border border-border-secondary">
            {clauses.map((clause, i) => (
              <div
                key={clause.title}
                className={`p-8 lg:p-10 ${
                  i < clauses.length - 1 ? 'border-b border-border-secondary' : ''
                }`}
              >
                <h2 className="text-sm font-semibold uppercase tracking-[0.1em] text-text-primary mb-4">
                  {clause.title}
                </h2>
                <div className="text-sm text-text-secondary leading-relaxed whitespace-pre-line">
                  {clause.content}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
